/**
 * A stored color made safe to fill a tile that carries a WHITE glyph — the Modules drawer's
 * app-icon squares (module-icon.tsx). Module colors (usrmgt.Application.Color,
 * tnt.TenantModule.Color) were partly chosen for an old navy popover, so many are pastels
 * (#42FFFF, #FFE4B5) that would put white on near-white.
 *
 * Keeps the hue, clamps lightness into a band white reads on, and lifts washed-out saturation.
 * Greys stay grey (slate), so a deliberately neutral module does not turn into a colour.
 * Anything unparseable — including no color at all — falls back to slate rather than failing
 * the tile.
 *
 * Moved here from Merchant.Web's side-bar/rail-palette.ts on 2026-10-04 so Tenant.Web's drawer
 * uses the same rule.
 */
export const readableTileTone = (hex?: string): string => {
  const m = /^#?([0-9a-f]{6})$/i.exec(( hex || '' ).trim());
  if (!m) return 'hsl(215 16% 47%)';
  const n = parseInt(m[1], 16);
  const [r, g, b] = [( n >> 16 ) & 255, ( n >> 8 ) & 255, n & 255].map(c => c / 255);
  const max = Math.max(r, g, b), min = Math.min(r, g, b), l = ( max + min ) / 2, d = max - min;
  let h = 0, s = 0;
  if (d > 0) {
    s = d / ( 1 - Math.abs(2 * l - 1) );
    h = max === r ? ( ( g - b ) / d ) % 6 : max === g ? ( b - r ) / d + 2 : ( r - g ) / d + 4;
    h = ( h * 60 + 360 ) % 360;
  }
  const grey = s < 0.12;
  const hue = grey ? 215 : Math.round(h);
  const sat = grey ? 16 : Math.round(Math.min(Math.max(s, 0.5), 0.9) * 100);
  // Yellow → green → cyan look far brighter than blue or red at the same lightness, so they get a
  // lower ceiling; otherwise #A5FF3D and #42FFFF stay neon and white glyphs wash out on them.
  const ceiling = !grey && hue >= 45 && hue <= 195 ? 0.42 : 0.5;
  const light = Math.round(Math.min(Math.max(l, 0.36), ceiling) * 100);
  return `hsl(${hue} ${sat}% ${light}%)`;
};
